import React, { useState, useEffect } from 'react';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';
import { SkeletonActivityFeed, EmptyState } from '@/components/ui/hayat';
import axios from 'axios';

/**
 * Phase 15.4 - Premium Activity Timeline
 * With time grouping, motion, and empty states
 */
function FamilyActivity() {
  const { household } = useFamilyAuth();
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (household) {
      loadActivities();
    }
  }, [household]);

  useEffect(() => {
    applyFilter();
  }, [filter, activities]);

  const loadActivities = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/activity/${household.id}?limit=50`,
        { withCredentials: true }
      );
      
      // Backend returns { activities: [...], count: ..., ... }
      const activitiesData = Array.isArray(response.data) 
        ? response.data 
        : response.data.activities || [];
      
      setActivities(activitiesData);
    } catch (error) {
      console.error('Fehler beim Laden der Aktivitäten:', error);
      if (error.response?.status === 401) {
        console.error('Session abgelaufen. Bitte neu anmelden.');
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = () => {
    if (filter === 'all') {
      setFilteredActivities(activities);
    } else if (filter === 'emergency') {
      setFilteredActivities(activities.filter(a => a.type === 'emergency'));
    } else if (filter === 'call') {
      setFilteredActivities(activities.filter(a => 
        a.type === 'call_started' || a.type === 'call_ended'
      ));
    } else if (filter === 'reminder') {
      setFilteredActivities(activities.filter(a => a.type === 'reminder_trigger'));
    }
  };

  const getActivityIcon = (type) => {
    const icons = {
      'emergency': '🚨',
      'call_started': '📞',
      'call_ended': '📞',
      'reminder_trigger': '⏰',
      'device_online': '✅',
      'device_offline': '🔴',
      'senior_created': '➕',
      'settings_updated': '⚙️',
      'family_user_registered': '👤'
    };
    return icons[type] || '📊';
  };

  const getActivityLabel = (type) => {
    const labels = {
      'emergency': 'Notfall',
      'call_started': 'Anruf gestartet',
      'call_ended': 'Anruf beendet',
      'reminder_trigger': 'Erinnerung',
      'device_online': 'Gerät online',
      'device_offline': 'Gerät offline',
      'senior_created': 'Senior hinzugefügt',
      'settings_updated': 'Einstellungen geändert',
      'family_user_registered': 'Benutzer registriert'
    };
    return labels[type] || type;
  };

  const getActivityColor = (type) => {
    if (type === 'emergency') return 'from-[#EF4444] to-[#dc2626]';
    if (type === 'call_started' || type === 'call_ended') return 'from-[#6DCEE8] to-[#3a9dbe]';
    if (type === 'reminder_trigger') return 'from-[#F6B352] to-[#d49640]';
    if (type === 'device_online') return 'from-[#22C55E] to-[#16a34a]';
    return 'from-neutral-600 to-neutral-700';
  };

  // Group activities by time
  const groupActivitiesByTime = (activities) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups = {
      'Heute': [],
      'Gestern': [],
      'Diese Woche': [],
      'Älter': []
    };

    activities.forEach(activity => {
      const activityDate = new Date(activity.created_at);
      
      if (activityDate >= today) {
        groups['Heute'].push(activity);
      } else if (activityDate >= yesterday) {
        groups['Gestern'].push(activity);
      } else if (activityDate >= new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)) {
        groups['Diese Woche'].push(activity);
      } else {
        groups['Älter'].push(activity);
      }
    });

    return groups;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050816] pb-24 page-transition">
        <div className="bg-neutral-900/60 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="animate-skeleton-pulse bg-neutral-700 h-10 w-64 rounded-2xl mb-2"></div>
            <div className="animate-skeleton-pulse bg-neutral-700 h-6 w-32 rounded-xl"></div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <SkeletonActivityFeed count={5} />
        </div>
      </div>
    );
  }

  const filterButtons = [
    { id: 'all', label: 'Alle', icon: '📋' },
    { id: 'emergency', label: 'Notfälle', icon: '🚨' },
    { id: 'call', label: 'Anrufe', icon: '📞' },
    { id: 'reminder', label: 'Erinnerungen', icon: '⏰' }
  ];

  const groupedActivities = groupActivitiesByTime(filteredActivities);

  return (
    <div className="min-h-screen bg-[#050816] pb-24 page-transition">
      {/* Header */}
      <div className="bg-neutral-900/60 backdrop-blur-xl border-b border-neutral-600/40 sticky top-0 z-40 glass-effect">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="animate-fade-in-down">
            <h1 className="text-3xl font-bold text-[#F9FAFB] mb-1">
              Aktivitäten 📊
            </h1>
            <p className="text-neutral-500 font-medium">
              {filteredActivities.length} Einträge
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2 stagger-item stagger-delay-1">
          {filterButtons.map(btn => (
            <button
              key={btn.id}
              onClick={() => setFilter(btn.id)}
              className={`
                px-6 py-3 rounded-2xl font-semibold text-sm whitespace-nowrap
                transition-all duration-300 ease-spring-soft flex items-center gap-2
                ${
                  filter === btn.id
                    ? 'bg-[#5B4B8A] text-white shadow-hayat-lg scale-105'
                    : 'bg-white text-[#CBD5F5] hover:bg-neutral-800/50 shadow-hayat-sm border border-hayat-neutral-200 hover:scale-105'
                }
              `}
            >
              <span className="text-lg">{btn.icon}</span>
              {btn.label}
            </button>
          ))}
        </div>

        {/* Activity Timeline */}
        {filteredActivities.length === 0 ? (
          <div className="animate-fade-in-up">
            <EmptyState
              icon="📊"
              title="Keine Aktivitäten"
              description="Es wurden noch keine Aktivitäten aufgezeichnet"
            />
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedActivities).map(([timeGroup, groupActivities], groupIdx) => {
              if (groupActivities.length === 0) return null;

              return (
                <div key={timeGroup} className="stagger-item" style={{ animationDelay: `${(groupIdx + 2) * 100}ms` }}>
                  {/* Time Group Header */}
                  <div className="flex items-center gap-4 mb-4">
                    <h3 className="text-lg font-bold text-[#F9FAFB]">{timeGroup}</h3>
                    <div className="flex-1 h-px bg-gradient-to-r from-neutral-600 to-transparent"></div>
                  </div>

                  {/* Activities in this group */}
                  <div className="space-y-3 relative pl-8 before:content-[''] before:absolute before:left-[11px] before:top-0 before:bottom-0 before:w-px before:bg-gradient-to-b before:from-[#5B4B8A]/30 before:via-[#6DCEE8]/30 before:to-transparent">
                    {groupActivities.map((activity, idx) => (
                      <div
                        key={activity.id}
                        className="relative animate-slide-up"
                        style={{ animationDelay: `${idx * 50}ms` }}
                      >
                        {/* Timeline dot */}
                        <div className={`absolute -left-[21px] top-5 w-6 h-6 rounded-full bg-gradient-to-br ${getActivityColor(activity.type)} shadow-hayat-md border-4 border-white z-10`}></div>

                        {/* Activity Card */}
                        <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-hayat-md hover:shadow-hayat-lg transition-all duration-300 border border-neutral-600/40 group hover:-translate-y-1">
                          <div className="flex items-start gap-4">
                            {/* Icon */}
                            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${getActivityColor(activity.type)} flex items-center justify-center text-2xl shadow-hayat-sm flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                              {getActivityIcon(activity.type)}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-4 mb-2">
                                <h4 className="text-lg font-bold text-[#F9FAFB]">
                                  {getActivityLabel(activity.type)}
                                </h4>
                                <span className="text-sm text-neutral-500 whitespace-nowrap">
                                  {new Date(activity.created_at).toLocaleString('de-DE', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>

                              {activity.description && (
                                <p className="text-[#CBD5F5] mb-3">
                                  {activity.description}
                                </p>
                              )}

                              {activity.senior_name && (
                                <div className="inline-flex items-center gap-2 px-3 py-1 bg-neutral-800/50 rounded-xl text-sm font-medium text-[#CBD5F5]">
                                  <span>👤</span>
                                  {activity.senior_name}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default FamilyActivity;
